import { useCallback, useEffect } from "react";
import { TimberClass, CreateTimberClass, UpdateTimberClass } from "../types/models/TimberClass.d";
import { UpdateEntityTimberClass } from "../types/models/TimberClass";
import { useGetTimberClassListQuery, useInsertTimberClassMutation, useUpdateTimberClassMutation, useDeleteTimberClassMutation, } from "../apis/TimberClass.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useTimberClass(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertTimberClassMutation, { 
        isLoading: insertTimberClassLoading, 
        isSuccess: insertTimberClassIsSuccess,
        isError: insertTimberClassIsError,
        error: insertTimberClassError,
    }] = useInsertTimberClassMutation()
    const [updateTimberClassMutation, { 
        isLoading: updateTimberClassLoading,
        isSuccess: updateTimberClassIsSuccess,
        isError: updateTimberClassIsError,
        error: updateTimberClassError,
    }] = useUpdateTimberClassMutation()
    const [deleteTimberClassMutation, { 
        isLoading: deleteTimberClassLoading,
        isSuccess: deleteTimberClassIsSuccess,
        isError: deleteTimberClassIsError,
        error: deleteTimberClassError,
    }] = useDeleteTimberClassMutation()

    const loading =
        insertTimberClassLoading ||
        updateTimberClassLoading ||
        deleteTimberClassLoading ||
        false

    const insertTimberClass = useCallback(async (timberClass: CreateTimberClass, redirectPath: string|undefined) => {
        try {
            const data = await insertTimberClassMutation(timberClass).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertTimberClassMutation, navigate])
    
    useEffect(() => {
        if (insertTimberClassIsSuccess) {
            openSuccessAlert('Timber Class created')
        } else if (insertTimberClassIsError) {
            console.log("add error", insertTimberClassError)
            openFailAlert(t`Timber Class add error: ` + insertTimberClassError?.data?.message)
        }
    }, [
        insertTimberClassIsSuccess,
        insertTimberClassIsError,
        insertTimberClassError
    ]);

    const updateTimberClass = useCallback(async (timberClass: UpdateEntityTimberClass, redirectPath: string|undefined) => {
        try {
            const data = await updateTimberClassMutation(timberClass).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateTimberClassMutation, navigate])
    
    useEffect(() => {
        if (updateTimberClassIsSuccess) {
            openSuccessAlert(t`Timber Class updated`)
        } else if (updateTimberClassIsError) {
            console.log("update error", updateTimberClassError)
            openFailAlert(t`Timber Class update error: ` + updateTimberClassError?.data?.message)
        }
    }, [
        updateTimberClassIsSuccess,
        updateTimberClassIsError,
        updateTimberClassError
    ]);

    const deleteTimberClass = useCallback((timberClass: TimberClass, redirectPath: string|undefined) => {
        openDialog(async () => {
            timberClass = {
                ...timberClass,
            }
            try {
                const { data } = await deleteTimberClassMutation(timberClass);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteTimberClassMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteTimberClassIsSuccess) {
            openSuccessAlert(t`Timber Class deleted`)
        } else if (deleteTimberClassIsError) {
            console.log("delete error", deleteTimberClassError)
            openFailAlert(t`Timber Class delete error: ` + deleteTimberClassError?.data?.message)
        }
    }, [
        deleteTimberClassIsSuccess,
        deleteTimberClassIsError,
        deleteTimberClassError
    ]);

    return {
        loading,
        insertTimberClass,
        updateTimberClass,
        deleteTimberClass
    }
}
